import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Clock, Check, ChefHat, Truck, Package, RefreshCw, XCircle, Trash2, AlertTriangle, UtensilsCrossed, Banknote, Printer, Edit2, Plus, Minus, Search, X, History, Calendar, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import API_URL from "@/utils/api";

const API = API_URL;

const STATUS_CONFIG = {
  new: { label: "Nuevo", icon: Clock, color: "bg-orange-100 text-orange-800" },
  pending: { label: "Pendiente", icon: Clock, color: "bg-yellow-100 text-yellow-800" },
  confirmed: { label: "Confirmado", icon: Check, color: "bg-blue-100 text-blue-800" },
  preparing: { label: "Preparando", icon: ChefHat, color: "bg-purple-100 text-purple-800" },
  ready: { label: "Listo", icon: Package, color: "bg-cyan-100 text-cyan-800" },
  on_the_way: { label: "En camino", icon: Truck, color: "bg-orange-100 text-orange-800" },
  delivered: { label: "Entregado", icon: Check, color: "bg-green-100 text-green-800" },
  completed: { label: "Entregado", icon: Check, color: "bg-green-100 text-green-800" }, // For legacy/demo compatibility
  cancelled: { label: "Anulado", icon: XCircle, color: "bg-red-100 text-red-800" },
};

// Status transitions depend on order type
const getStatusTransitions = (orderType, currentStatus) => {
  const deliveryTransitions = {
    new: ["preparing"],
    pending: ["preparing"],  // Skip confirmed - go directly to preparing
    confirmed: ["preparing"],
    preparing: ["ready"],
    ready: ["on_the_way"],
    on_the_way: ["delivered"],
    delivered: [],
    cancelled: [],
  };

  // LOCAL and PICKUP orders go directly to delivered (no on_the_way)
  const localTransitions = {
    new: ["preparing"],
    pending: ["preparing"],  // Skip confirmed - go directly to preparing
    confirmed: ["preparing"],
    preparing: ["ready"],
    ready: ["delivered"], // LOCAL/PICKUP orders go directly to delivered
    delivered: [],
    cancelled: [],
  };

  // Pickup and local use same transitions
  return (orderType === "local" || orderType === "pickup")
    ? localTransitions[currentStatus] || []
    : deliveryTransitions[currentStatus] || [];
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const { orderId } = useParams();

  // Si hay un orderId en la URL, filtrar por ese pedido inicialmente
  const [searchFilter, setSearchFilter] = useState(orderId || "");

  // Dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editOrderData, setEditOrderData] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [adminPassword, setAdminPassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // History states
  const [viewMode, setViewMode] = useState("active"); // active, history
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilters, setHistoryFilters] = useState({
    preset: 'today',
    status: 'all',
    type: 'all',
    q: '',
    offset: 0,
    limit: 20
  });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedHistoryOrder, setSelectedHistoryOrder] = useState(null);
  const [showHistoryDetail, setShowHistoryDetail] = useState(false);

  const fetchOrders = async () => {
    try {
      const branchParam = selectedBranch && selectedBranch !== "all" ? `?branch_id=${selectedBranch}` : "";
      const response = await axios.get(`${API}/admin/orders${branchParam}`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/public/branches`);
      setBranches(response.data);

      // If user has assigned branch, pre-select it
      if (user?.assigned_branches?.length > 0) {
        setSelectedBranch(user.assigned_branches[0]);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API}/menu`);
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching menu:", error);
    }
  };

  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const { preset, status, type, q, offset, limit } = historyFilters;
      const params = new URLSearchParams({
        preset,
        status,
        order_type: type,
        q,
        limit,
        offset,
        branch_id: selectedBranch
      });
      const res = await axios.get(`${API}/admin/orders/history?${params.toString()}`, { withCredentials: true });
      setHistoryOrders(res.data.orders);
      setHistoryTotal(res.data.total);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Error al cargar historial");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === 'history') {
      setViewMode('history');
    } else {
      setViewMode('active');
    }
  }, [location.search]);

  useEffect(() => {
    fetchBranches();
    fetchMenu();
  }, []);

  useEffect(() => {
    if (viewMode === "active") {
      fetchOrders();
    } else {
      fetchHistory();
    }
  }, [selectedBranch, viewMode, historyFilters]);

  // Refresh interval only for active orders
  useEffect(() => {
    if (viewMode !== "active") return;
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [selectedBranch, viewMode]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success("Estado actualizado");
      fetchOrders();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  // Mark LOCAL order as paid when customer pays at register
  const handleMarkAsPaid = async (orderId) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/pay`,
        {},
        { withCredentials: true }
      );
      toast.success("Pedido cobrado correctamente");
      fetchOrders();
    } catch (error) {
      toast.error("Error al cobrar el pedido");
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;

    try {
      await axios.put(
        `${API}/orders/${selectedOrder.order_id}/status`,
        { status: "cancelled" },
        { withCredentials: true }
      );
      toast.success("Pedido anulado correctamente");
      setCancelDialogOpen(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error("Error al anular el pedido");
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;

    // Check password for non-admins
    if (user?.role !== "admin" && !adminPassword) {
      toast.error("Contraseña de administrador requerida");
      return;
    }

    setIsDeleting(true);
    try {
      if (user?.role === "admin") {
        // Direct delete for admin
        await axios.delete(
          `${API}/admin/orders/${selectedOrder.order_id}`,
          { withCredentials: true }
        );
      } else {
        // Secure delete with password for others
        await axios.post(
          `${API}/admin/orders/${selectedOrder.order_id}/secure-delete`,
          { admin_password: adminPassword },
          { withCredentials: true }
        );
      }

      toast.success("Pedido eliminado correctamente");
      setDeleteDialogOpen(false);
      setSelectedOrder(null);
      setAdminPassword("");
      fetchOrders();
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.detail || "Error al eliminar el pedido";
      toast.error(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  const openCancelDialog = (order) => {
    setSelectedOrder(order);
    setCancelDialogOpen(true);
  };

  const openDeleteDialog = (order) => {
    setSelectedOrder(order);
    setDeleteDialogOpen(true);
  };

  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setEditOrderData({ ...order });
    setEditDialogOpen(true);
  };

  const handleUpdateItemQuantity = (index, delta) => {
    const newItems = [...editOrderData.items];
    const newQty = newItems[index].quantity + delta;
    if (newQty > 0) {
      newItems[index].quantity = newQty;
      // Recalcular total del pedido
      const newSubtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
      const deliveryFee = editOrderData.order_type === 'delivery' ? 2.5 : 0;
      const newTotal = newSubtotal + deliveryFee;
      setEditOrderData({ ...editOrderData, items: newItems, total: newTotal, subtotal: newSubtotal });
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = editOrderData.items.filter((_, i) => i !== index);
    const newSubtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
    const deliveryFee = editOrderData.order_type === 'delivery' ? 2.5 : 0;
    const newTotal = newSubtotal + deliveryFee;
    setEditOrderData({ ...editOrderData, items: newItems, total: newTotal, subtotal: newSubtotal });
  };

  const addProductToOrder = (product) => {
    const newItems = [...editOrderData.items];
    const existingIndex = newItems.findIndex(i => i.item_id === product.item_id);

    if (existingIndex >= 0) {
      newItems[existingIndex].quantity += 1;
    } else {
      newItems.push({
        item_id: product.item_id,
        name: product.name,
        price: product.price,
        quantity: 1,
        extras: [],
        notes: ""
      });
    }

    const newSubtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
    const deliveryFee = editOrderData.order_type === 'delivery' ? 2.5 : 0;
    const newTotal = newSubtotal + deliveryFee;

    setEditOrderData({ ...editOrderData, items: newItems, total: newTotal, subtotal: newSubtotal });
    setProductSearch(""); // Limpiar búsqueda
    toast.success(`${product.name} añadido al pedido`);
  };

  const handleSaveEdit = async () => {
    if (editOrderData.items.length === 0) {
      toast.error("El pedido debe tener al menos un producto");
      return;
    }

    setIsSavingEdit(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.order_id}`, {
        items: editOrderData.items,
        table_number: editOrderData.table_number,
        customer_name: editOrderData.customer_name,
        customer_phone: editOrderData.customer_phone,
        total: editOrderData.total,
        notes: editOrderData.notes
      }, { withCredentials: true });

      toast.success("Pedido actualizado correctamente");
      setEditDialogOpen(false);
      fetchOrders();
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Error al actualizar el pedido");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleViewHistoryDetail = async (order) => {
    try {
      const res = await axios.get(`${API}/admin/orders/${order.order_id}`, { withCredentials: true });
      setSelectedHistoryOrder(res.data);
      setShowHistoryDetail(true);
    } catch (error) {
      console.error("Error fetching order detail:", error);
      toast.error("Error al cargar detalles");
    }
  };

  const handleReprint = async (target) => {
    if (!selectedHistoryOrder) return;
    try {
      await axios.post(`${API}/admin/orders/${selectedHistoryOrder.order_id}/reprint`, { target }, { withCredentials: true });
      toast.success("Reimpresión encolada");
      setSelectedHistoryOrder(prev => ({
        ...prev,
        [target === 'customer' ? 'customer_print_status' : 'kitchen_print_status']: 'pending'
      }));
    } catch (error) {
      toast.error("Error al solicitar reimpresión");
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchOrders();
  };

  // Filter out completed orders (delivered + paid) - they shouldn't appear in the active orders list
  const activeOrders = orders.filter(order => {
    // Show cancelled orders (for reference)
    if (order.status === "cancelled") return true;
    // Hide orders that are delivered AND paid (completed)
    if (order.status === "delivered" && order.payment_status === "paid") return false;
    // Show all other orders
    return true;
  });

  const filteredOrders = activeOrders.filter(order => {
    // Filtrar por estado
    if (statusFilter && order.status !== statusFilter) return false;
    // Filtrar por búsqueda (ID de pedido)
    if (searchFilter) {
      const query = searchFilter.toLowerCase();
      return order.order_id.toLowerCase().includes(query) ||
        (order.customer_name || "").toLowerCase().includes(query) ||
        (order.table_number || "").toString().includes(query);
    }
    return true;
  });

  const pendingCount = activeOrders.filter((o) => ["new", "pending", "confirmed", "preparing", "ready", "on_the_way"].includes(o.status)).length;

  if (isLoading) {
    return (
      <AdminLayout title="Pedidos">
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Pedidos">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-yellow-500">PEDIDOS</h1>
            <p className="text-gray-400">
              {pendingCount > 0 ? (
                <span className="text-yellow-500 font-medium">{pendingCount} pedidos activos</span>
              ) : (
                "Sin pedidos pendientes"
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Branch Selector (Only for Admin) */}
            {(user?.role === "admin") && (
              <div className="w-48">
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="bg-gray-900 border-gray-800 text-white h-10">
                    <SelectValue placeholder="Todas las sucursales" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800 text-white">
                    <SelectItem value="all">Todas las sucursales</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="border-gray-700 text-gray-300 hover:text-yellow-500 hover:border-yellow-500"
              data-testid="refresh-orders-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex bg-gray-900 p-1 rounded-xl self-start gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setViewMode("active")}
            className={cn(
              "px-6 rounded-lg font-bold text-xs uppercase tracking-widest",
              viewMode === "active" ? "bg-yellow-500 text-black shadow-lg" : "text-gray-500"
            )}
          >
            Pedidos Activos
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setViewMode("history")}
            className={cn(
              "px-6 rounded-lg font-bold text-xs uppercase tracking-widest",
              viewMode === "history" ? "bg-blue-600 text-white shadow-lg" : "text-gray-500"
            )}
          >
            <History className="w-3.5 h-3.5 mr-2" />
            Historial
          </Button>
        </div>

        {viewMode === "active" ? (
          <>
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Buscar por ID, cliente o mesa..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-10 bg-gray-900 border-gray-800 text-white"
              />
              {searchFilter && (
                <button
                  onClick={() => setSearchFilter("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Status Filter Tabs */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={statusFilter === "" ? "default" : "outline"}
                onClick={() => setStatusFilter("")}
                className={statusFilter === ""
                  ? "bg-yellow-500 text-black hover:bg-yellow-400"
                  : "border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"}
                data-testid="filter-all"
              >
                Todos ({activeOrders.length})
              </Button>
              {Object.entries(STATUS_CONFIG).map(([key, { label, color }]) => {
                const count = activeOrders.filter(o => o.status === key).length;
                if (count === 0 && key !== "pending") return null; // Hide empty filters except pending
                return (
                  <Button
                    key={key}
                    size="sm"
                    variant={statusFilter === key ? "default" : "outline"}
                    onClick={() => setStatusFilter(key)}
                    className={statusFilter === key
                      ? "bg-yellow-500 text-black hover:bg-yellow-400"
                      : "border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"}
                    data-testid={`filter-${key}`}
                  >
                    {label} ({count})
                  </Button>
                );
              })}
            </div>
          </>
        ) : (
          /* History Filters */
          <div className="flex flex-wrap gap-3 items-center bg-gray-900 p-4 rounded-xl border border-gray-800">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Select
                value={historyFilters.preset}
                onValueChange={(v) => setHistoryFilters(prev => ({ ...prev, preset: v, offset: 0 }))}
              >
                <SelectTrigger className="w-[140px] bg-gray-800 border-gray-700 text-white h-9 text-xs">
                  <SelectValue placeholder="Periodo" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="yesterday">Ayer</SelectItem>
                  <SelectItem value="last7">Últimos 7 días</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Select
              value={historyFilters.status}
              onValueChange={(v) => setHistoryFilters(prev => ({ ...prev, status: v, offset: 0 }))}
            >
              <SelectTrigger className="w-[140px] bg-gray-800 border-gray-700 text-white h-9 text-xs">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white">
                <SelectItem value="all">Cualquier Estado</SelectItem>
                <SelectItem value="paid">Pagados</SelectItem>
                <SelectItem value="cancelled">Cancelados</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                className="pl-10 h-9 bg-gray-800 border-gray-700 text-white"
                placeholder="Buscar en historial (ID, Mesa, Cliente)..."
                value={historyFilters.q}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, q: e.target.value, offset: 0 }))}
              />
            </div>
          </div>
        )}

        {/* Orders List */}
        {/* Orders List */}
        {viewMode === "active" ? (
          filteredOrders.length === 0 ? (
            <div className="text-center py-12 bg-gray-900 rounded-xl border border-gray-800">
              <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No hay pedidos {statusFilter && `con estado "${STATUS_CONFIG[statusFilter]?.label}"`}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order) => {
                const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                const orderType = order.order_type || "delivery";
                const isLocal = orderType === "local";
                const isPickup = orderType === "pickup";
                const isLocalOrPickup = isLocal || isPickup;
                const nextStatuses = getStatusTransitions(orderType, order.status);
                const canCancel = !["delivered", "cancelled"].includes(order.status);

                // Badge config based on order type
                const orderTypeBadge = {
                  local: { class: "bg-green-500 text-black", label: "🍽️ LOCAL" },
                  pickup: { class: "bg-yellow-500 text-black", label: "🛍️ RECOGER" },
                  delivery: { class: "bg-blue-500 text-white", label: "🛵 DELIVERY" }
                }[orderType] || { class: "bg-gray-500", label: orderType };

                return (
                  <div
                    key={order.order_id}
                    data-testid={`order-${order.order_id}`}
                    className={`bg-gray-900 rounded-xl border border-gray-800 overflow-hidden ${order.status === "cancelled" ? "opacity-60" : ""
                      }`}
                  >
                    {/* Order Header */}
                    <div className="p-4 border-b border-gray-800">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-heading font-bold text-lg text-white">
                              #{order.order_id.slice(-6)}
                            </span>
                            {/* Order Type Badge */}
                            <Badge className={orderTypeBadge.class}>
                              {orderTypeBadge.label}
                            </Badge>
                            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                            <Badge variant={order.payment_status === "paid" ? "default" : "secondary"}
                              className={order.payment_status === "paid" ? "bg-green-600" : "bg-gray-700"}>
                              {order.payment_status === "paid" ? "Pagado" : "Pendiente pago"}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-400 mt-1">
                            {new Date(order.created_at).toLocaleString("es-ES")} • {order.user_name}
                            {isLocal && order.table_number && (
                              <span className="text-green-400 font-medium"> • Mesa {order.table_number}</span>
                            )}
                            {isPickup && (
                              <span className="text-yellow-400 font-medium"> • Para recoger</span>
                            )}
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* NOTE: Cobrar button removed - payment should only be done from TPV/Caja */}

                          {/* Status progression buttons */}
                          {nextStatuses.map((status) => (
                            <Button
                              key={status}
                              size="sm"
                              className="bg-yellow-500 hover:bg-yellow-400 text-black"
                              onClick={() => handleStatusChange(order.order_id, status)}
                              data-testid={`order-${order.order_id}-${status}`}
                            >
                              {STATUS_CONFIG[status].label}
                            </Button>
                          ))}

                          {/* Edit button */}
                          {!["delivered", "cancelled"].includes(order.status) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white"
                              onClick={() => navigate(`/admin/pos?edit_order=${order.order_id}`)}
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                          )}

                          {/* Cancel button */}
                          {canCancel && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                              onClick={() => openCancelDialog(order)}
                              data-testid={`order-${order.order_id}-cancel`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Anular
                            </Button>
                          )}

                          {/* Delete button */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                            onClick={() => openDeleteDialog(order)}
                            data-testid={`order-${order.order_id}-delete`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>

                          {/* RE-PRINT BUTTONS */}
                          <div className="flex gap-1 border-l border-gray-700 pl-2 ml-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-gray-400 hover:text-white hover:bg-gray-800 h-8 px-2"
                              title="Re-imprimir Ticket Cliente"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/orders/${order.order_id}/reprint/customer`, {}, { withCredentials: true });
                                  toast.success("Ticket enviado a impresora");
                                } catch (e) {
                                  toast.error("Error al imprimir ticket");
                                }
                              }}
                            >
                              <Printer className="w-3.5 h-3.5 mr-1" />
                              Ticket
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-orange-400 hover:text-orange-300 hover:bg-gray-800 h-8 px-2"
                              title="Re-imprimir Comanda Cocina"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/orders/${order.order_id}/reprint/kitchen`, {}, { withCredentials: true });
                                  toast.success("Comanda enviada a cocina");
                                } catch (e) {
                                  toast.error("Error al imprimir comanda");
                                }
                              }}
                            >
                              <Printer className="w-3.5 h-3.5 mr-1" />
                              Cocina
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order Details */}
                    <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Items */}
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Productos</p>
                        <div className="space-y-1">
                          {order.items.map((item, i) => (
                            <p key={i} className="text-sm text-white">
                              {item.quantity}x {item.name}
                              {item.extras?.length > 0 && (
                                <span className="text-gray-400">
                                  {" "}(+{item.extras.map((e) => e.name).join(", ")})
                                </span>
                              )}
                            </p>
                          ))}
                        </div>
                      </div>

                      {/* Address / Table Info */}
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">
                          {isLocal ? "Ubicación" : "Dirección"}
                        </p>
                        {isLocal ? (
                          <div className="flex items-center gap-2 text-sm text-white">
                            <UtensilsCrossed className="w-4 h-4 text-green-500" />
                            <span>
                              {order.table_number ? `Mesa ${order.table_number}` : "Recogida en mostrador"}
                            </span>
                          </div>
                        ) : order.address ? (
                          <p className="text-sm text-white">
                            {order.address.street}, {order.address.postal_code} {order.address.city}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500">Sin dirección</p>
                        )}
                        {order.notes && (
                          <p className="text-sm text-gray-400 mt-1 italic">
                            Nota: {order.notes}
                          </p>
                        )}
                      </div>

                      {/* Total */}
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-500 mb-2">Total</p>
                        <p className="text-2xl font-bold text-yellow-500">{order.total.toFixed(2)} €</p>
                        <p className="text-xs text-gray-400">
                          {order.payment_method === "card" ? "Tarjeta" : "Efectivo"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* History View List */
          <div className="space-y-4">
            {isLoadingHistory ? (
              <div className="flex justify-center py-20">
                <RefreshCw className="w-8 h-8 text-yellow-500 animate-spin" />
              </div>
            ) : historyOrders.length === 0 ? (
              <div className="text-center py-12 bg-gray-900 rounded-xl border border-gray-800">
                <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No se encontraron pedidos en el historial</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3">
                  {historyOrders.map(order => (
                    <div
                      key={order.order_id}
                      onClick={() => handleViewHistoryDetail(order)}
                      className="bg-gray-900 p-4 rounded-xl border border-gray-800 flex items-center justify-between hover:border-blue-500 cursor-pointer transition-colors group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-800 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-gray-400">
                          #{order.order_id.slice(-4).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-bold">{order.customer_name || `Mesa ${order.table_number}`}</span>
                            <Badge className={
                              order.status === 'cancelled' || order.payment_status === 'cancelled' ? 'bg-red-600' :
                                order.payment_status === 'paid' ? 'bg-green-600' :
                                  'bg-yellow-600'
                            }>
                              {order.status === 'cancelled' || order.payment_status === 'cancelled' ? 'ANULADO' :
                                order.payment_status === 'paid' ? 'COBRADO' : 'PENDIENTE'}
                            </Badge>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {new Date(order.created_at).toLocaleString()} • {order.order_type.toUpperCase()}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-6">
                        <div>
                          <div className="text-yellow-500 font-black text-lg">{(order.total || 0).toFixed(2)}€</div>
                          <div className="text-[10px] text-gray-600 font-mono">{order.order_id}</div>
                        </div>
                        <Eye className="w-5 h-5 text-gray-600 group-hover:text-blue-400 transition-colors" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-gray-500">Total: {historyTotal} pedidos</div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={historyFilters.offset === 0}
                      onClick={() => setHistoryFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                      className="border-gray-800 text-gray-400"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={historyFilters.offset + historyFilters.limit >= historyTotal}
                      onClick={() => setHistoryFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                      className="border-gray-800 text-gray-400"
                    >
                      Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Anular Pedido
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              ¿Estás seguro de que quieres anular el pedido <strong className="text-white">#{selectedOrder?.order_id.slice(-6)}</strong>?
              <br /><br />
              El cliente será notificado de la anulación. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              className="border-gray-700 text-gray-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCancelOrder}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="confirm-cancel-order"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Sí, anular pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Order Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setAdminPassword(""); // Limpiar pass al cerrar
      }}>
        <DialogContent className="bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Eliminar Pedido
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              ¿Estás seguro de que quieres eliminar permanentemente el pedido <strong className="text-white">#{selectedOrder?.order_id.slice(-6)}</strong>?
              <br /><br />
              <span className="text-red-400">⚠️ Esta acción eliminará el pedido de la base de datos y no se puede recuperar.</span>
            </DialogDescription>
          </DialogHeader>

          {/* Password Input for Non-Admins */}
          {user?.role !== "admin" && (
            <div className="space-y-2 py-2">
              <label className="text-sm font-medium text-gray-300">Contraseña de Administrador requerida</label>
              <Input
                type="password"
                placeholder="Introduce clave de admin"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-gray-700 text-gray-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteOrder}
              disabled={user?.role !== "admin" && !adminPassword}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-order"
            >
              {isDeleting ? "Verificando..." : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sí, eliminar pedido
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-xl">
              <Edit2 className="w-6 h-6 text-blue-500" />
              Editar Pedido #{selectedOrder?.order_id.slice(-6)}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Modifica los productos, la mesa o los datos del cliente.
            </DialogDescription>
          </DialogHeader>

          {editOrderData && (
            <div className="space-y-6 my-4">
              {/* Sección de añadir productos */}
              <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                <label className="text-xs text-gray-400 uppercase font-bold mb-2 block">Añadir Producto al Pedido</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="Buscar producto por nombre..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-10 bg-gray-900 border-gray-800 text-white"
                  />
                  {productSearch && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-xl max-h-48 overflow-y-auto">
                      {menuItems
                        .filter(item => item.name.toLowerCase().includes(productSearch.toLowerCase()))
                        .map(item => (
                          <button
                            key={item.item_id}
                            className="w-full text-left px-4 py-2 hover:bg-gray-700 text-white flex justify-between items-center"
                            onClick={() => addProductToOrder(item)}
                          >
                            <span>{item.name}</span>
                            <span className="text-yellow-500 font-bold">{item.price.toFixed(2)} €</span>
                          </button>
                        ))}
                      {menuItems.filter(item => item.name.toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
                        <div className="px-4 py-2 text-gray-500">No se encontraron productos</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {editOrderData.order_type === 'local' && (
                  <div>
                    <label className="text-xs text-gray-400 uppercase font-bold mb-1 block">Número de Mesa</label>
                    <Input
                      value={editOrderData.table_number || ""}
                      onChange={(e) => setEditOrderData({ ...editOrderData, table_number: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Ej: 15"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-400 uppercase font-bold mb-1 block">Nombre Cliente</label>
                  <Input
                    value={editOrderData.customer_name || ""}
                    onChange={(e) => setEditOrderData({ ...editOrderData, customer_name: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase font-bold mb-2 block">Productos</label>
                <div className="space-y-2">
                  {editOrderData.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-800 p-3 rounded-lg border border-gray-700">
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.name}</p>
                        <p className="text-xs text-gray-400">{item.price.toFixed(2)} € / unidad</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
                          <button
                            onClick={() => handleUpdateItemQuantity(index, -1)}
                            className="p-1.5 hover:bg-gray-800 text-gray-400"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center text-white font-bold">{item.quantity}</span>
                          <button
                            onClick={() => handleUpdateItemQuantity(index, 1)}
                            className="p-1.5 hover:bg-gray-800 text-yellow-500"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase font-bold mb-1 block">Notas de Pedido</label>
                <textarea
                  value={editOrderData.notes || ""}
                  onChange={(e) => setEditOrderData({ ...editOrderData, notes: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white text-sm min-h-[80px]"
                />
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4 flex justify-between items-center">
                <span className="text-blue-400 font-bold">NUEVO TOTAL:</span>
                <span className="text-2xl font-black text-white">{editOrderData.total.toFixed(2)} €</span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0 mt-6">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-gray-700 text-gray-300 flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
              className="bg-blue-600 hover:bg-blue-500 text-white font-bold flex-1"
            >
              {isSavingEdit ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Detail Dialog with Timeline */}
      <Dialog open={showHistoryDetail} onOpenChange={setShowHistoryDetail}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-4 border-b border-gray-800 flex flex-row items-center justify-between">
            <div>
              <DialogTitle className="text-blue-400 text-xl font-black">
                DETALLES DEL PEDIDO #{selectedHistoryOrder?.order_id.slice(-6).toUpperCase()}
              </DialogTitle>
              <div className="flex gap-2 mt-1">
                <Badge variant="outline" className="border-gray-700 text-gray-400 text-[10px]">
                  {new Date(selectedHistoryOrder?.created_at).toLocaleString()}
                </Badge>
                <Badge className={selectedHistoryOrder?.payment_status === 'paid' ? 'bg-green-500 text-black' : 'bg-yellow-500 text-black'}>
                  {selectedHistoryOrder?.payment_status === 'paid' ? 'COBRADO' : 'PENDIENTE'}
                </Badge>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setShowHistoryDetail(false)} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-4 flex gap-6">
            {/* Info Column */}
            <div className="flex-1 space-y-4">
              <section className="bg-gray-800/50 rounded-lg p-4 border border-gray-800">
                <h4 className="text-xs font-black text-gray-500 uppercase mb-3 tracking-widest">Resumen de Productos</h4>
                <div className="space-y-2">
                  {selectedHistoryOrder?.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <div className="flex gap-2">
                        <span className="text-yellow-500 font-bold">{item.quantity}x</span>
                        <span className="text-gray-200">{item.name}</span>
                      </div>
                      <span className="text-white font-mono">{(item.price * item.quantity).toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-gray-700 flex justify-between font-black text-lg">
                  <span className="text-gray-400">TOTAL</span>
                  <span className="text-yellow-500">{(selectedHistoryOrder?.total || 0).toFixed(2)}€</span>
                </div>
              </section>

              <section className="bg-gray-800/50 rounded-lg p-4 border border-gray-800">
                <h4 className="text-xs font-black text-gray-500 uppercase mb-3 tracking-widest">Estado de Impresión</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-[10px] text-gray-500 uppercase font-bold">Ticket Cliente</p>
                    <div className="flex items-center gap-2">
                      <Badge className={selectedHistoryOrder?.customer_print_status === 'printed' ? 'bg-green-600' : 'bg-yellow-600'}>
                        {selectedHistoryOrder?.customer_print_status === 'printed' ? 'Impreso' : 'Pendiente/Error'}
                      </Badge>
                      <Button size="icon" variant="outline" className="h-7 w-7 border-gray-700" onClick={() => handleReprint('customer')}>
                        <Printer className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] text-gray-500 uppercase font-bold">Comanda Cocina</p>
                    <div className="flex items-center gap-2">
                      <Badge className={selectedHistoryOrder?.kitchen_print_status === 'printed' ? 'bg-green-600' : 'bg-yellow-600'}>
                        {selectedHistoryOrder?.kitchen_print_status === 'printed' ? 'Impreso' : 'Pendiente'}
                      </Badge>
                      <Button size="icon" variant="outline" className="h-7 w-7 border-gray-700" onClick={() => handleReprint('kitchen')}>
                        <Printer className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Audit Timeline Column */}
            <div className="w-80 bg-black/20 rounded-lg p-4 border border-gray-800 flex flex-col">
              <h4 className="text-xs font-black text-gray-500 uppercase mb-4 tracking-widest flex items-center gap-2">
                <Clock className="w-3 h-3" /> Auditoría / Historial
              </h4>
              <div className="flex-1 space-y-6 relative border-l border-gray-800 ml-2 pl-6 overflow-y-auto pr-2">
                {selectedHistoryOrder?.events?.map((event, idx) => (
                  <div key={idx} className="relative">
                    {/* Dot */}
                    <div className={`absolute -left-[31px] w-2.5 h-2.5 rounded-full border-2 border-gray-900 ${event.type.includes('ok') ? 'bg-green-500' :
                      event.type.includes('failed') ? 'bg-red-500' :
                        event.type.includes('created') ? 'bg-blue-500' :
                          'bg-yellow-500'
                      }`} />

                    <p className="text-[10px] text-gray-500 font-mono">
                      {new Date(event.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </p>
                    <p className="text-xs font-bold text-gray-200 mt-0.5">
                      {event.type.replace(/_/g, ' ').toUpperCase()}
                    </p>
                    {event.actor_role && (
                      <p className="text-[9px] text-blue-400 font-black uppercase mt-0.5">
                        Actor: {event.actor_role}
                      </p>
                    )}
                    {event.meta && Object.keys(event.meta).length > 0 && (
                      <div className="mt-1 bg-white/5 rounded p-1.5 text-[10px] text-gray-400 font-mono">
                        {JSON.stringify(event.meta)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="p-4 bg-gray-800/50 border-t border-gray-800">
            <Button variant="outline" className="border-gray-700 text-gray-300" onClick={() => setShowHistoryDetail(false)}>
              Cerrar Detalle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

